import {
  RegenerateQuestionDataInput,
  RegenerateQuestionMutation,
  useRegenerateQuestionMutation,
} from "@generated/graphql";
import { ToastContext } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import typography from "constants/typography.constants";
import { GenerationType, QuestionInputs } from "../types";

interface UseGenerateQuestionProps {
  successText: string;
  generationTypes?: GenerationType[];
  skillId: string | undefined;
}

const useRegenerateQuestion = ({ successText, generationTypes, skillId }: UseGenerateQuestionProps) => {
  const { addToast } = useContext(ToastContext);
  const form = useFormContext<QuestionInputs>();
  const [regenerateQuestion, { loading }] = useRegenerateQuestionMutation();

  const handleRegenerateQuestion = () => {
    if (!skillId) {
      console.log("Отсутствует skillId");
      return addToast({ text: typography.errors.pleaseRepeat, type: "warning" });
    }

    const { criterion, grade, ...formData } = form.getValues();

    if (!grade?.id) {
      return addToast({ text: "Для генерации необходимо выбрать Грейд", type: "warning" });
    }

    let input: RegenerateQuestionDataInput = {
      updateCriteria: false,
      updateQuestion: false,
      updateReferenceAnswer: false,
      grade: grade.id,
      language: formData.language,
      skillId,
      time: Number(formData.time),
      criterion: criterion.map((criteria) => criteria.value),
      text: formData.text,
      referenceAnswer: formData.referenceAnswer,
    };

    if (!generationTypes) {
      input = {
        ...input,
        answerComment: formData.answerComment,
        questionComment: formData.questionComment,
        criteriaComment: formData.criteriaComment,
        updateCriteria: true,
        updateQuestion: true,
        updateReferenceAnswer: true,
      };
    } else {
      input = {
        ...input,
        ...(generationTypes.includes("criteria")
          ? { updateCriteria: true, criteriaComment: formData.criteriaComment }
          : {}),
        ...(generationTypes.includes("question")
          ? { updateQuestion: true, questionComment: formData.questionComment }
          : {}),
        ...(generationTypes.includes("referenceAnswer")
          ? { updateReferenceAnswer: true, answerComment: formData.answerComment }
          : {}),
      };

      let inputEntries = Object.entries(input);

      if (!input.updateCriteria && !input.criterion?.length) {
        inputEntries = inputEntries.filter(([key]) => key !== "criterion");
      }

      if (!input.updateQuestion && !input.text) {
        inputEntries = inputEntries.filter(([key]) => key !== "text");
      }

      if (!input.updateReferenceAnswer && !input.referenceAnswer) {
        inputEntries = inputEntries.filter(([key]) => key !== "referenceAnswer");
      }

      input = Object.fromEntries(inputEntries) as RegenerateQuestionDataInput;
    }

    return regenerateQuestion({
      variables: {
        input,
      },
    })
      .then((res) => {
        const fieldsKeys: (keyof Omit<RegenerateQuestionMutation["regenerateQuestionSync"], "__typename">)[] = [];

        if (input.updateCriteria) {
          fieldsKeys.push("criterion");
        }

        if (input.updateQuestion) {
          fieldsKeys.push("text");
        }

        if (input.updateReferenceAnswer) {
          fieldsKeys.push("referenceAnswer");
        }

        const data = res.data?.regenerateQuestionSync;

        fieldsKeys.forEach((key) => {
          const field = data?.[key];

          if (field !== null && field !== undefined) {
            const value = Array.isArray(field) ? field.map((criteria) => ({ value: criteria })) : field;
            form.setValue(key, value);
          }
        });

        addToast({ text: successText, type: "success" });
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  return [handleRegenerateQuestion, { loading }] as const;
};

export default useRegenerateQuestion;
